import dayjs from "dayjs";
import type { SeriesLineOptions } from "highcharts";

import { colorsSets } from "@similarweb/styles";

import type { ChartData, LegendItem, LineGraphSeries, SelectedCountry } from "../types";

const dateToUTC = (dateString: string): number => {
    return dayjs.utc(dateString).valueOf();
};

/**
 * Sorts an array of objects with Country property by their order in a reference array of country IDs
 * Items not found in the reference array are placed at the end
 */
export const sortByCountryOrder = <T extends { Country: number }>(items: T[], countryOrderReference: number[]): T[] => {
    return items.toSorted((a, b) => {
        const posA = countryOrderReference.indexOf(a.Country);
        const posB = countryOrderReference.indexOf(b.Country);

        // Items not found in reference (-1) should be placed at the end
        const adjustedPosA = posA === -1 ? Number.MAX_SAFE_INTEGER : posA;
        const adjustedPosB = posB === -1 ? Number.MAX_SAFE_INTEGER : posB;

        return adjustedPosA - adjustedPosB;
    });
};

/**
 * Helper to compute deterministic list of countries to show (1:1 with table order)
 * - `orderedTopCountryIds` if provided takes precedence when no selectedCountries
 * - for selectedCountries: reorder them by their position in orderedTopCountryIds to match table order
 * - assigns deterministic colors for generated countries while avoiding duplicates when possible
 */
const computeCountriesToShow = (
    data: ChartData,
    selectedCountries: SelectedCountry[],
    orderedTopCountryIds?: number[],
    maxCountries: number = 10,
): SelectedCountry[] => {
    if (!data || Object.keys(data).length === 0) {
        return [];
    }

    // If user has explicit selections, reorder them by table position for consistent display
    if (selectedCountries && selectedCountries.length > 0) {
        if (!orderedTopCountryIds?.length) {
            return selectedCountries;
        }

        // Sort selected countries by their position in the table (highest to lowest value)
        return sortByCountryOrder(selectedCountries, orderedTopCountryIds);
    }

    // Build deterministic list from orderedTopCountryIds when provided, otherwise fallback to object keys
    const topIds: number[] = orderedTopCountryIds
        ? orderedTopCountryIds.slice(0, maxCountries)
        : Object.keys(data)
              .slice(0, maxCountries)
              .map((id) => Number(id));

    const colors = colorsSets.c.toArray();
    const usedColors = new Set<string>();

    const getNextColor = (startIndex: number) => {
        // Find first color not already used, starting from startIndex for determinism
        const len = colors.length;
        for (let offset = 0; offset < len; offset++) {
            const idx = (startIndex + offset) % len;
            const color = colors[idx];
            if (!usedColors.has(color)) {
                usedColors.add(color);
                return color;
            }
        }

        // If all colors used, fallback to deterministic modulo
        const fallback = colors[startIndex % len];
        usedColors.add(fallback);
        return fallback;
    };

    const result: SelectedCountry[] = [];
    let colorIndex = 0;

    for (const id of topIds) {
        const key = String(id);
        if (!data[key]) {
            continue;
        }

        const selectionColor = getNextColor(colorIndex);
        colorIndex += 1;

        const countryObj: Partial<SelectedCountry> = {};
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore - dynamic assignment to match legacy SelectedCountry shape
        countryObj.Country = id;
        countryObj.selectionColor = selectionColor;

        result.push(countryObj as SelectedCountry);
    }

    return result;
};

export const transformChartDataToSeries = (
    data: ChartData,
    selectedCountries: SelectedCountry[],
    countryNameFilter: (countryId: string | number) => string,
    orderedTopCountryIds?: number[],
): LineGraphSeries[] => {
    if (!data || Object.keys(data).length === 0) {
        return [];
    }

    const countriesToShow = computeCountriesToShow(data, selectedCountries, orderedTopCountryIds);

    return countriesToShow
        .map((country) => {
            const countryId = String(country.Country);
            const countryData = data[countryId];

            if (!countryData) {
                return null;
            }

            const dataPoints: SeriesLineOptions["data"] = Object.entries(countryData)
                .filter(([dateString]) => dateString)
                .map(([dateString, value]): [number, number | null] => [dateToUTC(dateString), value])
                .sort((a, b) => a[0] - b[0]);

            if (dataPoints.length === 0) {
                return null;
            }

            return {
                name: countryNameFilter(country.Country),
                color: country.selectionColor,
                id: String(country.Country),
                visible: true,
                data: dataPoints,
            };
        })
        .filter((series): series is LineGraphSeries => Boolean(series));
};

export const generateLegendItems = (
    selectedCountries: SelectedCountry[],
    chartData: ChartData,
    countryNameFilter: (countryId: string | number) => string,
    orderedTopCountryIds?: number[],
): LegendItem[] => {
    const countriesToShow = computeCountriesToShow(chartData, selectedCountries, orderedTopCountryIds);

    return countriesToShow
        .filter((country) => chartData[String(country.Country)])
        .map((country) => ({
            name: countryNameFilter(country.Country),
            color: country.selectionColor,
            countryId: country.Country,
        }));
};

export const filterChartData = (
    data: ChartData,
    selectedCountries: SelectedCountry[],
    orderedTopCountryIds?: number[],
    maxCountries: number = 10,
): ChartData => {
    if (!data) {
        return {};
    }

    // If we have selected countries, show only those
    if (selectedCountries && selectedCountries.length > 0) {
        const selectedCountryIds = selectedCountries.map((c) => String(c.Country));
        const filtered: ChartData = {} as ChartData;
        for (const countryId of selectedCountryIds) {
            if (data[countryId]) {
                filtered[countryId] = data[countryId];
            }
        }
        return filtered;
    }

    // If selectedCountries is an empty array, user has cleared selection - show empty chart
    if (selectedCountries && selectedCountries.length === 0) {
        return {};
    }

    // If selectedCountries is undefined/null (shouldn't happen with current setup), fallback to top countries
    if (orderedTopCountryIds && orderedTopCountryIds.length > 0) {
        const idsToInclude = orderedTopCountryIds.slice(0, maxCountries).map((id) => String(id));
        return idsToInclude
            .filter((countryId) => data[countryId])
            .reduce((filtered, countryId) => {
                filtered[countryId] = data[countryId];
                return filtered;
            }, {} as ChartData);
    }

    const countryIds = Object.keys(data).slice(0, maxCountries);
    return countryIds.reduce((filtered, countryId) => {
        filtered[countryId] = data[countryId];
        return filtered;
    }, {} as ChartData);
};
